import React from "react";
import useSWR from "swr";
import { useLocation, useParams } from "react-router-dom";
import { Text, Footer } from "@vagastec/ensemble-core";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { fetcherWithToken } from "./fetcher-with-token";
import {
  containerPaginaVagaStyle,
  infoVagaStyle,
  linkCandidaturaStyle,
  separatorStyle,
  headerStyle,
  confidencialStyle,
  imgSeparatorStyle,
  mainContentStyle,
  mainContentSpaceStyle,
  titleStyle,
  infoPcdStyle,
  iconTextStyle,
  containerInfoCompanyStyle,
  infoCompanyStyle,
  containerMapStyle,
  shareIconStyle,
  footerStyle,
  infoFooterStyle,
  copyrightStyle,
  mapStyle,
  loaderStyle,
  containerErrorMessage,
} from "./pagina.vaga.css";
import {
  benefitItemPreview,
  listBenefits,
  previewLogoStyle,
  previewTagListStyle,
  previewTagStyle,
} from "./step-4.css";
import { IlustracaoPredio } from "./illustration/ilustracao-predio";
import { vars } from "./theme.css";
import { IconesBeneficios } from "./icons/icones-beneficios";
import { IconePcd } from "./icons/icone-pcd";
import { IconeDolar } from "./icons/Icone-dolar";
import { IconeMapa } from "./icons/Icone-mapa";
import { IconeIdCard } from "./icons/icone-id-card";
import { Space } from "./components/space";
import { IconFacebook } from "./icons/icon-facebook";
import { IconLinkedin } from "./icons/icon-linkedin";
import { IconTwitter } from "./icons/icon-twitter";
import { IconWhatsApp } from "./icons/icon-whatsapp";
import { LogoVagas } from "./icons/logo-vagas";
import CardLoader from "./components/card-loader";
import { Erro } from "./tela-erro";

export function PaginaVaga() {
  const idLegado = location.pathname.slice(2);
  const intl = useIntl();

  const {
    data: codigoLegado,
    error: codigoLegadoError,
    isValidating: codigoLegadoIsValidating,
  } = useSWR(
    `${process.env.HESTIA}/anuncio_publicado/${idLegado}`,
    fetcherWithToken
  );

  const {
    data: vagaData,
    error: vagaError,
    isValidating: vagaIsValidating,
  } = useSWR(
    () =>
      codigoLegado ? `${process.env.HESTIA}/anuncios/${codigoLegado.id}` : null,
    fetcherWithToken
  );

  const mapRef = React.useCallback((element) => {
    if (window.google && element) {
      const latLng = {
        lat: vagaData?.localizacao.latitude,
        lng: vagaData?.localizacao.longitude,
      };

      const map = new google.maps.Map(element, {
        zoom: 15,
        center: latLng,
      });

      const marker = new google.maps.Marker({
        position: latLng,
        map: map,
      });
    }
  }, []);

  const currencyIdToIso = {
    1: "BRL",
    2: "USD",
  };

  function exibirSalario() {
    if (vagaData?.salario.combinar === false) {
      return `${new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: currencyIdToIso[vagaData?.salario?.moeda_id],
      }).format(vagaData?.salario.minimo)} - ${new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: currencyIdToIso[vagaData?.salario?.moeda_id],
      }).format(vagaData?.salario.maximo)}`;
    }

    if (vagaData?.salario.combinar) {
      return (
        <>
          <FormattedMessage
            id="step4.salarioCombinar"
            defaultMessage="Salário a combinar"
          />
        </>
      );
    }
  }

  function diasParaEncerrar() {
    let dataHoje = new Date().toISOString().slice(0, 10);
    let dataHojeFormatada = new Date(dataHoje);
    let dataExpiracao = vagaData?.data_expiracao;
    let dataExpiracaoFormatada = new Date(dataExpiracao);
    let diferencaMilisegundos = dataExpiracaoFormatada - dataHojeFormatada;
    let diferencaDias = diferencaMilisegundos / (24 * 60 * 60 * 1000);
    return diferencaDias;
  }

  return (
    <>
      {!codigoLegadoIsValidating &&
      !vagaIsValidating &&
      !codigoLegadoError &&
      !vagaError &&
      !vagaData.suspender_vaga ? (
        <div
          style={{
            backgroundColor: vars.color.white,
          }}
        >
          <header>
            <div className={headerStyle}>
              {vagaData?.confidencial ? (
                <div className={confidencialStyle}>
                  <IlustracaoPredio aria-hidden />
                  <div className={imgSeparatorStyle}></div>
                  <Text as="p" fontColor={vars.color.lightForeground}>
                    <FormattedMessage
                      id="paginaVaga.empresaConfidencial"
                      defaultMessage="Empresa confidencial"
                    />
                  </Text>
                </div>
              ) : (
                <img
                  className={previewLogoStyle}
                  src={vagaData?.modelo_de_apresentacao.logotipo}
                  alt="logotipo da empresa"
                ></img>
              )}
            </div>
          </header>

          <main>
            <div className={mainContentStyle}>
              <div className={mainContentSpaceStyle}>
                <div className={containerPaginaVagaStyle}>
                  <div>
                    <div className={titleStyle}>
                      <Text as="h1" size="lg" weight={600}>
                        {vagaData?.titulo}
                      </Text>

                      {vagaData?.pcd ? (
                        <div className={infoPcdStyle}>
                          <IconePcd
                            fill={vars.color.foreground}
                            width={20}
                            height={20}
                          />
                          <Text
                            size="sm"
                            weight={600}
                            fontColor={vars.color.foreground}
                          >
                            <FormattedMessage
                              id="paginaVaga.vagaPcd"
                              defaultMessage="PCD"
                            />
                          </Text>
                        </div>
                      ) : null}
                    </div>

                    <Space height="1rem" />

                    <div className={infoVagaStyle}>
                      <div className={iconTextStyle}>
                        <IconeMapa
                          fill={vars.color.pinIcon}
                          width={20}
                          height={20}
                          style={{ flexShrink: 0 }}
                          aria-label={intl.formatMessage(
                            defineMessage({
                              id: "paginaVaga.iconeLocal",
                              defaultMessage: "Ícone local",
                            })
                          )}
                        />
                        <Space width=".25rem" />
                        <Text as="p" size="sm">
                          {vagaData?.localizacao.localizacao_completa}
                        </Text>
                      </div>

                      <Space width=".5rem" />

                      <div className={iconTextStyle}>
                        <IconeMapa
                          fill={vars.color.pinIcon}
                          width={20}
                          height={20}
                          style={{ flexShrink: 0 }}
                          aria-label={intl.formatMessage(
                            defineMessage({
                              id: "paginaVaga.iconeLocal",
                              defaultMessage: "Ícone local",
                            })
                          )}
                        />

                        <Space width=".25rem" />

                        <Text as="p" size="sm">
                          {vagaData?.modelo_local_trabalho.label}
                        </Text>
                      </div>

                      <Space height="1rem" />

                      <div className={iconTextStyle}>
                        <IconeIdCard
                          width={20}
                          height={20}
                          fill={vars.color.listUlIcon}
                          style={{ flexShrink: 0 }}
                          aria-label={intl.formatMessage(
                            defineMessage({
                              id: "paginaVaga.iconeNivel",
                              defaultMessage: "Ícone nível",
                            })
                          )}
                        />

                        <Space width=".25rem" />

                        <Text as="p" size="sm">
                          {vagaData?.nivel_hierarquico.label}
                        </Text>
                      </div>

                      <Space width="1rem" />

                      <div className={iconTextStyle}>
                        <IconeDolar
                          fill={vars.color.successForeground}
                          width={20}
                          height={20}
                          style={{ flexShrink: 0 }}
                          aria-label={intl.formatMessage(
                            defineMessage({
                              id: "paginaVaga.iconeSalario",
                              defaultMessage: "Ícone salário",
                            })
                          )}
                        />

                        <Space width=".25rem" />

                        <Text as="p" size="sm">
                          {exibirSalario()}
                        </Text>
                      </div>

                      <Space width="1rem" />

                      <div className={iconTextStyle}>
                        <IconeIdCard
                          fill={vars.color.pwd}
                          width={20}
                          height={20}
                          style={{ flexShrink: 0 }}
                          aria-label={intl.formatMessage(
                            defineMessage({
                              id: "paginaVaga.iconeModeloContratacao",
                              defaultMessage: "Ícone modelo de contratação",
                            })
                          )}
                        />

                        <Space width=".25rem" />

                        <Text as="p" size="sm">
                          {vagaData?.modelo_contratacao.label}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>

                <Space height="1rem" />
                <div className={separatorStyle}></div>

                <Space height="2rem" />
                <div>
                  <Text as="h2" weight={600} size="lg">
                    <FormattedMessage
                      id="step4.previewDescricao"
                      defaultMessage="Descrição"
                    />
                  </Text>
                  {
                    <div
                      style={{ overflow: "auto", overflowWrap: "break-word" }}
                      dangerouslySetInnerHTML={{
                        __html: vagaData?.descricao,
                      }}
                    ></div>
                  }

                  <Space height="2rem" />

                  <Text as="h2" weight={600} size="lg">
                    <FormattedMessage
                      id="step1.titleRequisitos"
                      defaultMessage="Requisitos"
                    />
                  </Text>

                  <Space height="1rem" />
                  <Text as="h3" weight={600}>
                    <FormattedMessage
                      id="step4.previewLabelEscolaridade"
                      defaultMessage={"Escolaridade mínima"}
                    />
                  </Text>

                  <Space height=".5rem" />

                  <Text size="sm" weight={600} as="p">
                    {" "}
                    <span className={previewTagStyle}>
                      {vagaData?.nivel_escolaridade.label}
                    </span>
                  </Text>

                  <Space height="1rem" />
                  {vagaData?.idiomas.length ? (
                    <div>
                      <Text as="h3" weight={600}>
                        <FormattedMessage
                          id="step1.labelTextIdiomas"
                          defaultMessage="Idiomas"
                        />
                      </Text>
                      <Space height=".5rem" />
                      <Text size="sm" weight={600} as="p">
                        <span className={previewTagListStyle}>
                          {vagaData?.idiomas.map((v) => (
                            <span key={v.id} className={previewTagStyle}>
                              {v.label}
                            </span>
                          ))}
                        </span>
                      </Text>
                    </div>
                  ) : null}

                  {vagaData?.requisitos ? (
                    <>
                      <Space height="1rem" />
                      <Text as="h3" weight={600}>
                        <FormattedMessage
                          id="step1.labelTextOutrosRequisitos"
                          defaultMessage="Outros requisitos"
                        />
                      </Text>

                      {
                        <div
                          dangerouslySetInnerHTML={{
                            __html: vagaData?.requisitos,
                          }}
                        ></div>
                      }
                    </>
                  ) : null}

                  {vagaData?.beneficios.length ? (
                    <>
                      <Space height="1rem" />
                      <Text size="lg" weight={600} as="h2">
                        <FormattedMessage
                          id="step1.titleBeneficios"
                          defaultMessage="Benefícios"
                        />
                      </Text>
                      <Space height="1rem" />
                      <div className={listBenefits}>
                        {vagaData?.beneficios.map((v) => (
                          <div className={benefitItemPreview} key={v.id}>
                            <IconesBeneficios
                              aria-hidden="true"
                              dangerouslySetInnerHTML={{
                                __html: v.imagem,
                              }}
                            />
                            <Text size="sm" as="p">
                              {v.label}
                            </Text>{" "}
                            {v.valor ? (
                              <Text size="xs" as="p">
                                {new Intl.NumberFormat("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                }).format(v.valor)}
                              </Text>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}

                  {!vagaData?.confidencial ? (
                    <>
                      <div className={containerInfoCompanyStyle}>
                        <div className={infoCompanyStyle}>
                          <div>
                            <img
                              className={previewLogoStyle}
                              src={vagaData?.modelo_de_apresentacao.logotipo}
                              alt={intl.formatMessage(
                                defineMessage({
                                  id: "paginaVaga.logotipo",
                                  defaultMessage: "logotipo da empresa",
                                })
                              )}
                            ></img>
                          </div>

                          <div>
                            <Text as="p" weight={600} size="md">
                              {vagaData?.modelo_de_apresentacao.empresa}
                            </Text>

                            <Space height=".5rem" />
                            <Text
                              as="p"
                              weight={600}
                              fontColor={vars.color.lightForeground}
                            >
                              {" "}
                              <FormattedMessage
                                id="step4.titleApresentacaoEmpresa"
                                defaultMessage="Apresentação da empresa"
                              />
                            </Text>
                          </div>
                        </div>

                        <div>
                          {
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  vagaData?.modelo_de_apresentacao.descricao,
                              }}
                            ></div>
                          }
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              <div>
                <div className={containerMapStyle}>
                  {vagaData?.confidencial === false ? (
                    <>
                      <div className={mapStyle} ref={mapRef}></div>
                    </>
                  ) : null}

                  <Space height="1.5rem" />

                  <Text size="lg" weight={600} as="p">
                    {vagaData?.titulo}
                  </Text>

                  <Space height="1.5rem" />
                  <a
                    href={`${process.env.VAGAS}/GoHome.asp?v=${idLegado}&j=t&ss=1`}
                    className={linkCandidaturaStyle}
                    // target="_self"
                    // rel="noopener noreferrer"
                  >
                    <FormattedMessage
                      id="paginaVaga.candidatura"
                      defaultMessage="Inscreva-se"
                    />
                  </a>

                  <Space height="1.5rem" />

                  <Text size="sm" as="p" fontColor={vars.color.lightForeground}>
                    {diasParaEncerrar()}{" "}
                    <FormattedMessage
                      id="paginaVaga.diasParaEncerrar"
                      defaultMessage="dias para encerrar esta vaga"
                    />
                  </Text>

                  <Space width="1.5rem" />

                  <div className={shareIconStyle}>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${process.env.VAGAS}/v${idLegado}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                      aria-label="Facebook"
                    >
                      <IconFacebook aria-hidden />
                    </a>

                    <a
                      href={`https://www.linkedin.com/shareArticle?url=${process.env.VAGAS}/v${idLegado}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                      aria-label="Linkedin"
                    >
                      <IconLinkedin aria-hidden />
                    </a>

                    <a
                      href={`http://twitter.com/share?url=${process.env.VAGAS}/v${idLegado}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                      aria-label="Twitter"
                    >
                      <IconTwitter aria-hidden />
                    </a>

                    <a
                      href={`https://wa.me/?text=${process.env.VAGAS}/v${idLegado}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                      aria-label="Whatsapp"
                    >
                      {" "}
                      <IconWhatsApp aria-hidden />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </main>

          <footer className={footerStyle}>
            <div className={separatorStyle} />

            <div className={infoFooterStyle}>
              <LogoVagas />
              <p className={copyrightStyle}>
                &copy; {new Date().getFullYear()} Vagas Tecnologia. Todos os
                direitos reservados.
              </p>
            </div>
          </footer>
        </div>
      ) : (
        <>
          {vagaIsValidating || codigoLegadoIsValidating ? (
            <>
              <Space height="18rem" />
              <div className={loaderStyle}>
                <div>
                  <CardLoader width={46} height={46} />
                  <CardLoader width={46} height={46} />
                  <CardLoader width={46} height={46} />
                </div>
                <Text
                  as="p"
                  fontColor={vars.color.lightForeground}
                  weight={600}
                  size="lg"
                >
                  <FormattedMessage
                    id="paginaVaga.carregando"
                    defaultMessage="Carregando..."
                  />
                </Text>
              </div>
            </>
          ) : codigoLegadoError || vagaError ? (
            <div className={containerErrorMessage}>
              <Space height="2rem" />
              <Erro />
            </div>
          ) : vagaData.suspender_vaga ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginTop: "16rem",
              }}
            >
              <Text as="p" fontColor={vars.color.lightForeground} weight={600}>
                <FormattedMessage
                  id="paginaVaga.tituloInscricoesEncerradas"
                  defaultMessage="Inscrições encerradas"
                />
              </Text>
              <Text as="p" fontColor={vars.color.lightForeground}>
                <FormattedMessage
                  id="paginaVaga.subtituloInscricoesEncerradas"
                  defaultMessage="A vaga que você tentou acessar se encontra com as inscrições encerradas. Para pesquisar outras vagas, acesse o site <a>www.vagas.com.br</a> "
                  values={{
                    a: (chunks) => (
                      <a
                        href="https://vagas.com.br"
                        target="_blank"
                        style={{
                          color: vars.color.green,
                          textDecoration: "none",
                        }}
                      >
                        {chunks}
                      </a>
                    ),
                  }}
                />
              </Text>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
